import type { IShowCatalog } from '@pkg/lib-data'
import { EUserJourneyType } from '@pkg/lib-data'

import { validateAvailabilityAndTransformDates } from './handlers'
import { defineProductCardDataShows, defineProductDetailsDataShows } from './shows'
import type { ProductCardDataShows, ProductDetailsDataShows } from './shows'

export interface CategoryProductsData {
  userJourneyType: EUserJourneyType
  byIds: Record<string, ProductDetailsDataShows>
  byIdsCatalogProducts: Record<string, ProductDetailsDataShows>
  extraData: null
  catalogsContentData: IShowCatalog[]
}

export const transformProductDetailsData = (args: {
  catalogsContentData: IShowCatalog[]
  userJourneyType: EUserJourneyType
  categoryId: string
  getAllowedPlatforms: () => any[]
}): CategoryProductsData => {
  const { catalogsContentData, userJourneyType, categoryId, getAllowedPlatforms } = args

  const payload: CategoryProductsData = {
    byIds: {},
    userJourneyType,
    extraData: null,
    catalogsContentData,
    byIdsCatalogProducts: {},
  }

  const debugStats = {
    totalProducts: 0,
    skippedNoProduct: 0,
    skippedNoDates: 0,
    skippedNoComputed: 0,
    created: 0,
  }

  for (const catalogContentItem of catalogsContentData) {
    const products = Object.entries(catalogContentItem.products || {})

    if (userJourneyType === EUserJourneyType.SHOWS) {
      const hiddenProducts = Object.entries(catalogContentItem.promotedProducts || {})

      products.push(...hiddenProducts)
    }

    debugStats.totalProducts += products.length

    for (const [id, product] of products) {
      if (!product) {
        debugStats.skippedNoProduct++
        continue
      }

      const datesNormalized = validateAvailabilityAndTransformDates({
        eventDate: product.date,
        userJourneyType,
        dealType: product.dealType,
      })

      if (!datesNormalized) {
        debugStats.skippedNoDates++
        continue
      }

      const { eventDate } = datesNormalized

      const computedProduct = defineProductDetailsDataShows({
        product,
        id,
        eventDate: eventDate as Date,
        categoryId,
        getAllowedPlatforms,
      })

      if (computedProduct) {
        payload.byIds[id] = computedProduct
        debugStats.created++
      } else {
        debugStats.skippedNoComputed++
      }
    }

    // For shows, all products are catalog products
    payload.byIdsCatalogProducts = payload.byIds
  }

  return payload
}

export const transformProductCardsData = (args: {
  catalogsContentData: IShowCatalog[]
  userJourneyType: EUserJourneyType
  categoryId: string
  productsMapFiltered: Record<string, ProductDetailsDataShows>
  productsMapOriginal: Record<string, ProductDetailsDataShows>
}): { default: ProductCardDataShows[] } => {
  const { catalogsContentData, userJourneyType, categoryId, productsMapFiltered, productsMapOriginal } = args

  if (userJourneyType === EUserJourneyType.SHOWS) {
    return defineProductCardDataShows({
      catalogsContentData,
      productsMapFiltered,
      productsMapOriginal,
      categoryId,
    })
  }

  return { default: [] }
}
